
import { Card, CardContent } from "@/components/ui/card";
import { useEffect } from "react";

interface TestimonialCardProps {
  name: string;
  role: string;
  content: string;
  rating: number;
}

const TestimonialCard = ({ name, role, content, rating }: TestimonialCardProps) => (
  <Card className="border-none shadow-none bg-transparent hover:bg-accent/5 transition-colors duration-300">
    <CardContent className="pt-6">
      <div className="flex items-center mb-4">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-4 h-4 ${i < rating ? "text-yellow-500" : "text-gray-300"}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <p className="text-muted-foreground mb-4">{content}</p>
      <div>
        <div className="font-semibold">{name}</div>
        <div className="text-sm text-muted-foreground">{role}</div>
      </div>
    </CardContent>
  </Card>
);

export const TestimonialsSection = () => {
  useEffect(() => {
    // Add structured data for testimonials
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "MindTube",
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Sarah Johnson"
          },
          "reviewBody": "MindTube transformed how I study. The AI insights help me grasp complex topics quickly."
        },
        // ... add other reviews
      ]
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-muted/50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">
          What Our Users Say
        </h2>
        <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
          Join thousands of learners who have transformed their YouTube experience with MindTube
        </p>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          <TestimonialCard
            name="Sarah Johnson"
            role="Student"
            content="MindTube transformed how I study. The AI insights help me grasp complex topics quickly."
            rating={5}
          />
          <TestimonialCard
            name="David Chen"
            role="Content Creator"
            content="As an educator, this tool helps me ensure my content is more accessible and engaging."
            rating={5}
          />
          <TestimonialCard
            name="Maria Garcia"
            role="Professional"
            content="Perfect for continuous learning. I use it daily for professional development."
            rating={5}
          />
        </div>
      </div>
    </section>
  );
};
